/**
 *
 * @module paymentMethodResponse
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import PaymentMethodType from './paymentMethodType';

const BillingAddress = Services.Commerce.BillingAddress;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    boolean,
    nonEmptyObject,
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class PaymentMethodResponse
 * @desc Response containing information on a payment method.
 * @note This class has the same name as SDK.Services.Commerce.PaymentMethodResponse but it has
 * a somewhat different purpose. The Services object is just meant to be a raw service reponse object. However,
 * since the UWP platform needs to be able to define distinct objects that are returned from each method,
 * this PaymentMethodResponse is meant to be the base class for other payment-specific classes such as
 * CardPaymentMethodResponse which has properties that are more relavent to credit cards. In the future,
 * there could potentially be a PaypalPaymentMethodResponse that has other properties relavent to that method.
 * SDK.Services.Commerce.PaymentMethodResponse would contain all the possible data for all payment methods and
 * the CommerceManager would be responsible for returning the appropriate SDK.Commerce.PaymentMethodResponse-derived
 * type.
 *
 */
export default class PaymentMethodResponse {

    /**
     *
     * @param {Object} options
     * @param {SDK.Services.Commerce.BillingAddress} options.billingAddress
     * @param {Boolean} options.isDefault
     * @param {String} options.paymentMethodId
     * @param {String} options.walletId
     * @param {SDK.Commerce.PaymentMethodType} options.type=PaymentMethodType.PaymentCard
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'PaymentMethodResponse(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get billingAddress() {
                return `${this.ctor} needs SDK.Services.Commerce.BillingAddress instance`;
            },
            get isDefault() {
                return `${this.ctor} options.isDefault should be a Boolean`;
            },
            get paymentMethodId() {
                return `${this.ctor} options.paymentMethodId should be a String with length`;
            },
            get walletId() {
                return `${this.ctor} options.walletId should be a String with length`;
            },
            get type() {
                return `${this.ctor} options.type should be a String with length`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { billingAddress, isDefault, paymentMethodId, walletId, type = PaymentMethodType.PaymentCard } = options;

        instanceStrict(billingAddress, BillingAddress, errorMessage.billingAddress, InvalidArgumentException);
        boolean(isDefault, errorMessage.isDefault, InvalidArgumentException);
        nonEmptyString(paymentMethodId, errorMessage.paymentMethodId, InvalidArgumentException);
        nonEmptyString(walletId, errorMessage.walletId, InvalidArgumentException);
        nonEmptyString(type, errorMessage.type, InvalidArgumentException);

        /**
         *
         * @access public
         * @type {SDK.Services.Commerce.BillingAddress}
         *
         */
        this.billingAddress = billingAddress;

        /**
         *
         * @access public
         * @type {Boolean}
         *
         */
        this.isDefault = isDefault;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.paymentMethodId = paymentMethodId;

        /**
         *
         * @access public
         * @type {String}
         *
         */
        this.walletId = walletId;

        /**
         *
         * @access public
         * @type {SDK.Commerce.PaymentMethodType}
         * @desc The type of payment method.
         *
         */
        this.type = PaymentMethodType[type] || PaymentMethodType.OTHER;

    }

}



// WEBPACK FOOTER //
// ./src/commerce/paymentMethodResponse.js